import { Component, Input, Output, EventEmitter, OnInit, AfterViewChecked, ChangeDetectorRef, AfterViewInit, ElementRef, ViewChild, ViewChildren, QueryList, OnChanges, SimpleChanges, OnDestroy, HostBinding } from '@angular/core';
import { ChatAutoScrollService } from 'src/shared/services/chat-auto-scroll/chat-auto-scroll.service';
import { Subscription } from 'rxjs';
import { ChatQueryItem } from '../../models';
import { ResponseFeedbackSubmitPayload } from 'src/shared/components/response-feedback/response-feedback.component';
import { ResponseOptionsMenuAction } from 'src/shared/components/response-options-menu/response-options-menu.component';
import { LoggerService } from '../../../../services/logger-services/logger.service';
import { AgentConfigService } from '../../services/agent-config/agent-config.service';
import { SharedResponseActionsComponent } from 'src/shared/components/response-actions/response-actions.component';
import type { AgentFilePreviewRequest } from 'src/shared/components/agent-file-attachment-card/agent-file-attachment-card.component';

/**
 * Agent Chat Interface Component
 * 
 * Displays the agent chat history and manages scrolling behavior.
 * Handles both manual and automatic scrolling during message streaming.
 * 
 * Features:
 * - Displays chat history with questions and answers
 * - Auto-scrolls during message streaming
 * - Shows scroll-to-bottom arrow when not at bottom
 * - Detects user scroll and pauses auto-scroll
 * - Handles pill/suggestion clicks
 * - Manages loading states
 * 
 * Usage:
 * <app-agent-chat-interface
 *   [ChatQueriesList]="chatHistory"
 *   [agentId]="currentAgentId"
 *   [loaderShow]="isLoading"
 *   [IsNewChat]="isNew"
 *   (scrollArrowChange)="onScrollArrowChange($event)"
 *   (pillClicked)="onPillClick($event)">
 * </app-agent-chat-interface>
 */
@Component({
  selector: 'app-agent-chat-interface',
  templateUrl: './agent-chat-interface.component.html',
  styleUrls: ['./agent-chat-interface.component.scss']
})
export class AgentChatInterfaceComponent implements OnInit, AfterViewChecked, AfterViewInit, OnChanges, OnDestroy {
  @Input() ChatQueriesList: ChatQueryItem[] = [];
  @Input() agentId: string = '';

  /** Enables ria-only styling hooks (e.g. markdown-rich-table layout) via :host-context(.ria-agent). */
  @HostBinding('class.ria-agent') get isRiaAgent(): boolean {
    return this.agentId === 'ria_agent';
  }

  /** Enables fixed-viewport rich tables for regulations (see markdown-rich-table shouldUseFixedViewport). */
  @HostBinding('class.regulations-agent') get isRegulationsAgentHost(): boolean {
    return this.agentId === 'regulations_agent';
  }
  @Input() loaderShow: boolean = false;
  @Input() isResponseStreaming: boolean = false;
  @Input() isConversationSaved: boolean = true;
  @Input() isFollowUpQuestionsReceived: boolean = false;
  @Input() IsNewChat: boolean = true;
  @Input() isLoadingMoreHistory: boolean = false;
  @Input() hasMoreHistory: boolean = false;
  @Input() showReasoning: boolean = false;
  @Input() reasoningSteps: any[] = [];
  @Input() activeReasoningIndex: number = 0;
  
  // Engaging Loader inputs
  @Input() showEngagingLoader: boolean = false;
  @Input() engagingLoaderIsStreaming: boolean = false;
  @Input() engagingLoaderIsCompleted: boolean = false;
  @Input() engagingLoaderSteps: any[] = [];
  @Input() engagingLoaderCurrentAction: string = '';
  /** Tenant ID for share/conversation (from chatbot context when embedded). */
  @Input() tenantId: string = '';
  /** Minimal config for metadata (sources): FusionAppUIUrl, DomainId, module_name. */
  @Input() configdata: { FusionAppUIUrl?: string; DomainId?: string; module_name?: string } = {};
  /** selected_module_name for metadata: view_access_to_regulations | view_access_to_rtq. */
  @Input() metadataModuleName: string = '';
  /** Whether metadata is currently being fetched (shows spinner in metadata header). */
  @Input() isMetadataLoading: boolean = false;
  /** Via: true from bubble_start to bubble_end – show dots loader below response (same position as regulations loader). */
  @Input() isBubblesLoading: boolean = false;
  /** When true, the last message uses slide-up animation (only set when user just asked in this session, not on thread switch). */
  @Input() animateLastMessage: boolean = false;
  /** Feedback options from config.json (same as chatbot v2 configdata.FeedbackOptions). */
  @Input() feedbackOptionsConfig: any[] = [];
  @Output() scrollArrowChange = new EventEmitter<boolean>();
  /** Emitted when user scrolls up so container can cancel pending scroll-to-bottom timeouts. */
  @Output() userScrolledUp = new EventEmitter<void>();
  @Output() pillClicked = new EventEmitter<string>();
  @Output() loadMoreHistory = new EventEmitter<void>();
  /** Emits when audio-copy updates ChatQueriesList (e.g. isSpeaking, isPaused). */
  @Output() ChatQueriesListEmit = new EventEmitter<any>();
  @Output() showSharePopup = new EventEmitter<any>();
  @Output() hideSharePopup = new EventEmitter<void>();
  @Output() universalLoading = new EventEmitter<boolean>();
  /** Emits when user selects Regenerate / Make crisp / Detailed from 3-dots menu. */
  @Output() responseOptionsMenuAction = new EventEmitter<{ chatdata: any; action: ResponseOptionsMenuAction }>();
  /** Emits when user clicks like on a response. Payload: { chatdata, isLike: boolean } */
  @Output() feedbackLikeClick = new EventEmitter<{ chatdata: any; isLike: boolean }>();
  /** Emits when user clicks dislike (undo). Payload: { chatdata, isReset: boolean } */
  @Output() feedbackDislikeUndo = new EventEmitter<{ chatdata: any }>();
  /** Emits when user submits dislike feedback form. Payload: { chatdata, payload: ResponseFeedbackSubmitPayload } */
  @Output() feedbackSubmit = new EventEmitter<{ chatdata: any; payload: ResponseFeedbackSubmitPayload }>();
  /** Metadata "close history" (no-op in agents). */
  @Output() metadataCloseHistory = new EventEmitter<void>();
  /** Metadata "display source panel" (no-op in agents). */
  @Output() metadataDisplaySourcePanel = new EventEmitter<any>();
  @Output() tableDownloaded = new EventEmitter<string>();
  @Output() tableDownloadFailed = new EventEmitter<void>();
  @Output() filePreviewRequested = new EventEmitter<AgentFilePreviewRequest>();
  @Output() filePreviewFailed = new EventEmitter<string>();
  @Output() fileDownloadCompleted = new EventEmitter<string>();
  @Output() fileDownloadFailed = new EventEmitter<string>();
  @ViewChild('scrollTarget', { static: false }) scrollTarget?: ElementRef;
  @ViewChildren(SharedResponseActionsComponent) responseActionsComponents!: QueryList<SharedResponseActionsComponent>;

  private previousChatListLength: number = 0;
  private previousLastAnswerLength: number = 0;
  private scrollStateSubscription?: Subscription;
  private boundScrollHandler: ((event: Event) => void) | null = null;
  private streamingScrollInterval: any = null;
  private lazyLoadThreshold: number = 150; // Pixels from top to trigger lazy load
  private isLazyLoadingTriggered: boolean = false;
  /** Last scrollTop; used to detect scroll-up and cancel container's scroll-to-bottom timeouts. */
  private lastScrollTopFromContainer: number = -1;

  private static readonly RIA_TEXT_LOADER_MS = 4000;
  /** RIA: true during the initial "Thinking..." text phase before dots may appear. */
  riaTextLoaderPhaseActive = false;
  private riaTextLoaderTimer: ReturnType<typeof setTimeout> | null = null;

  showScrollToBottomArrow: boolean = false;
  userHasScrolledUp: boolean = false;
  isNearBottom: boolean = true;

  /** Per-agent UI options from config (feedback, copy, audio, regenerate, share). Missing/undefined = show. */
  get agentOptions(): { feedback?: boolean; copy?: boolean; audio?: boolean; regenerate?: boolean; share?: boolean } {
    const config = this.agentConfigService.getAgentConfig(this.agentId);
    return config?.options ?? {};
  }

  /** RIA: thinking loader for the first 4 seconds before the dot loader appears. */
  get showRiaThinkingLoader(): boolean {
    return this.isRiaAgent
      && this.showEngagingLoader
      && !this.engagingLoaderIsCompleted
      && this.riaTextLoaderPhaseActive;
  }

  /** Dots loader before reasoning – RIA hides dots during the 4-second thinking phase; regulations uses the tail loader under thinking/answer instead. */
  get showDotsBeforeReasoning(): boolean {
    if (!this.showEngagingLoader || this.engagingLoaderIsCompleted) {
      return false;
    }
    if (this.isRiaAgent) {
      return !this.riaTextLoaderPhaseActive;
    }
    if (this.agentId === 'regulations_agent') {
      return false;
    }
    return true;
  }

  /**
   * Regulations: one dots loader under thinking, then under the growing answer, until the stream ends.
   * Same slot as VIA's isBubblesLoading loader.
   */
  get showRegulationsTailLoader(): boolean {
    if (this.agentId !== 'regulations_agent') {
      return false;
    }
    if (this.isResponseStreaming || this.isMetadataLoading) {
      return true;
    }
    return this.showEngagingLoader && !this.engagingLoaderIsCompleted;
  }

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly autoScrollService: ChatAutoScrollService,
    private readonly logger: LoggerService,
    private readonly agentConfigService: AgentConfigService
  ) {}

  /**
   * Initialize component and configure auto-scroll service
   */
  ngOnInit(): void {
    try {
      this.previousChatListLength = this.ChatQueriesList.length;
      if (this.ChatQueriesList.length > 0) {
        const lastMessage = this.ChatQueriesList[this.ChatQueriesList.length - 1];
        this.previousLastAnswerLength = lastMessage?.answer?.length || 0;
      }
      
      this.autoScrollService.configure({
        enableUserScrollDetection: true,
        enableScrollArrow: true,
        enableNearBottomDetection: true,
        throttleMs: 100,
        nearBottomThreshold: 100,
        scrollArrowThreshold: 120
      });

      this.autoScrollService.setCallbacks({
        onScrollArrowChange: (show: boolean) => {
          this.showScrollToBottomArrow = show;
          this.scrollArrowChange.emit(show);
        },
        onUserScroll: (state) => {
          this.userHasScrolledUp = state.userHasScrolledUp;
          this.isNearBottom = state.isNearBottom;
          if (state.userHasScrolledUp && this.streamingScrollInterval) {
            this.stopStreamingScroll();
          }
          if (!state.userHasScrolledUp && state.isNearBottom && this.loaderShow && !this.streamingScrollInterval) {
            setTimeout(() => {
              const stillNearBottom = !this.autoScrollService.isUserAwayFromBottom(100);
              if (!this.userHasScrolledUp && !this.streamingScrollInterval && stillNearBottom) {
                this.startStreamingScroll();
              }
            }, 0);
          }
        },
        onScrollToBottom: () => {
          // After arrow click or programmatic scroll to bottom: re-enable auto-scroll if still streaming.
          // Service has already set userHasScrolledUp to false, so we only need loaderShow and no active interval.
          const streamingUi =
            this.loaderShow || this.isResponseStreaming || !!this.ChatQueriesList?.[this.ChatQueriesList.length - 1]?.isLoading;
          if (streamingUi && !this.streamingScrollInterval) {
            this.startStreamingScroll();
          }
        }
      });

      this.scrollStateSubscription = this.autoScrollService.getScrollStateObservable().subscribe(state => {
        this.showScrollToBottomArrow = state.showScrollToBottomArrow;
        this.userHasScrolledUp = state.userHasScrolledUp;
        this.isNearBottom = state.isNearBottom;
        this.scrollArrowChange.emit(state.showScrollToBottomArrow);
      });
      
      this.autoScrollService.findChatContainer(
        ['.agent-chat-container', '.agent-chat-history-wrapper', '.chat-expandable-area-new', '.chat-right-box'],
        this.scrollTarget
      );
    } catch (error: any) {
      this.logger.logInfo('[AgentChatInterface] Error in ngOnInit', 'ngOnInit', error);
    }
  }

  /**
   * Detect answer streaming and manage auto-scroll.
   * Triggers: (1) ChatQueriesList last message answer length increase while loading,
   *          (2) loaderShow or isResponseStreaming true with last message loading (fallback so first chunk works).
   */
  ngOnChanges(changes: SimpleChanges): void {
    try {
      if (changes['showEngagingLoader'] || changes['agentId'] || changes['engagingLoaderIsCompleted']) {
        this.syncRiaTextLoaderPhase();
      }

      if (changes['ChatQueriesList'] && this.ChatQueriesList.length > 0) {
        const lastMessage = this.ChatQueriesList[this.ChatQueriesList.length - 1];
        const currentAnswerLength = lastMessage?.answer?.length || 0;
        const isStreaming = currentAnswerLength > this.previousLastAnswerLength && !!lastMessage?.isLoading;

        if (isStreaming) {
          this.previousLastAnswerLength = currentAnswerLength;
          const scrollState = this.autoScrollService.getScrollState();
          const awayFromBottom = this.autoScrollService.isUserAwayFromBottom(100);
          if (!this.streamingScrollInterval && !scrollState.userHasScrolledUp && !awayFromBottom) {
            this.startStreamingScroll();
          }
        } else if (!lastMessage?.isLoading && this.streamingScrollInterval) {
          this.stopStreamingScroll();
        }
      }

      // Fallback: start auto-scroll when streaming inputs are true and last message is loading
      // (handles first chunk or when list reference doesn't change every token)
      const lastMessage = this.ChatQueriesList.length > 0
        ? this.ChatQueriesList[this.ChatQueriesList.length - 1]
        : null;
      const streamingActive = (this.loaderShow || this.isResponseStreaming) && !!lastMessage?.isLoading;
      const scrollState = this.autoScrollService.getScrollState();
      const awayFromBottom = this.autoScrollService.isUserAwayFromBottom(100);
      if (streamingActive && !this.streamingScrollInterval && !scrollState.userHasScrolledUp && !awayFromBottom) {
        this.startStreamingScroll();
      }

      // When list is replaced (e.g. thread switch): scroll to bottom so the latest question is visible.
      // Only when reference changed (new array) and not streaming / not loading more.
      if (changes['ChatQueriesList'] && this.ChatQueriesList.length > 0 && !this.isLoadingMoreHistory) {
        const prev = changes['ChatQueriesList'].previousValue;
        const listRefChanged = !prev || prev !== this.ChatQueriesList;
        const last = this.ChatQueriesList[this.ChatQueriesList.length - 1];
        if (listRefChanged && !last?.isLoading) {
          // Do not reset when user deliberately scrolled up — resetScrollState clears userHasScrolledUp
          // and would cause an unwanted jump to bottom after streaming completes.
          if (!this.autoScrollService.getScrollState().userHasScrolledUp) {
            this.autoScrollService.resetScrollState();
          }
          this.lastScrollTopFromContainer = -1;
          const scrollToBottomIfNotUserScrolled = (): void => {
            if (this.autoScrollService.getScrollState().userHasScrolledUp) {
              return;
            }
            this.autoScrollService.findChatContainer(undefined, this.scrollTarget);
            this.autoScrollService.scrollToBottom('auto');
          };
          setTimeout(scrollToBottomIfNotUserScrolled, 0);
          setTimeout(scrollToBottomIfNotUserScrolled, 200);
          setTimeout(scrollToBottomIfNotUserScrolled, 500);
        }
      }
    } catch (error: any) {
      this.logger.logInfo('[AgentChatInterface] Error in ngOnChanges', 'ngOnChanges', error);
    }
  }

  /**
   * Start controlled auto-scroll during streaming
   * Uses interval to prevent excessive scroll calls.
   * Does not start if user has already scrolled up (avoids restart after list updates).
   * Does not start when streaming has ended (avoids restart from scroll/update after "Final complete message").
   */
  private startStreamingScroll(): void {
    try {
      const scrollState = this.autoScrollService.getScrollState();
      if (scrollState.userHasScrolledUp) {
        return;
      }
      if (this.autoScrollService.isUserAwayFromBottom(100)) {
        return;
      }
      if (!this.loaderShow && !this.isResponseStreaming) {
        return;
      }
      const lastMsg = this.ChatQueriesList?.length ? this.ChatQueriesList[this.ChatQueriesList.length - 1] : null;
      if (lastMsg && !lastMsg.isLoading && !this.loaderShow && !this.isResponseStreaming) {
        return;
      }
      this.stopStreamingScroll();
      this.streamingScrollInterval = setInterval(() => {
        const scrollState = this.autoScrollService.getScrollState();
        if (scrollState.userHasScrolledUp) {
          this.stopStreamingScroll();
          return;
        }
        if (this.loaderShow || this.isResponseStreaming) {
          this.autoScrollService.autoScrollDuringStreaming(true, 100);
        } else {
          this.stopStreamingScroll();
        }
      }, 150);
    } catch (error: any) {
      this.logger.logInfo('[AgentChatInterface] Error starting streaming scroll', 'startStreamingScroll', error);
    }
  }

  /**
   * Stop streaming scroll interval
   */
  private stopStreamingScroll(): void {
    try {
      if (this.streamingScrollInterval) {
        clearInterval(this.streamingScrollInterval);
        this.streamingScrollInterval = null;
      }
    } catch (error: any) {
      this.logger.logInfo('[AgentChatInterface] Error stopping streaming scroll', 'stopStreamingScroll', error);
    }
  }

  /**
   * Initialize scroll listener after view is ready
   */
  ngAfterViewInit(): void {
    try {
      setTimeout(() => {
        const container = this.autoScrollService.findChatContainer(
          ['.agent-chat-container', '.agent-chat-history-wrapper', '.chat-expandable-area-new', '.chat-right-box'],
          this.scrollTarget
        );
        
        if (container) {
          this.boundScrollHandler = this.onContainerScroll.bind(this);
          container.addEventListener('scroll', this.boundScrollHandler, { passive: true });
        }
        // Use 'auto' so we don't start a smooth scroll that can stop in the middle when content loads
        this.autoScrollService.scrollToBottom('auto');
        setTimeout(() => this.autoScrollService.scrollToBottom('auto'), 200);
        setTimeout(() => this.autoScrollService.scrollToBottom('auto'), 500);
      }, 100);
    } catch (error: any) {
      this.logger.logInfo('[AgentChatInterface] Error in ngAfterViewInit', 'ngAfterViewInit', error);
    }
  }

  /**
   * Scroll to bottom when new message is added
   */
  ngAfterViewChecked(): void {
    try {
      if (this.ChatQueriesList.length > this.previousChatListLength) {
        this.previousChatListLength = this.ChatQueriesList.length;
        this.autoScrollService.findChatContainer(undefined, this.scrollTarget);

        // Only scroll to bottom for new messages (user sent a question).
        // Skip during lazy loading (older messages prepended at top) so
        // scroll position restoration in onContainerScroll is not overridden.
        if (!this.isLoadingMoreHistory) {
          setTimeout(() => this.autoScrollService.scrollToBottom(), 100);
          setTimeout(() => this.autoScrollService.scrollToBottom(), 300);
        }
        
        if (this.ChatQueriesList.length > 0) {
          const lastMessage = this.ChatQueriesList[this.ChatQueriesList.length - 1];
          this.previousLastAnswerLength = lastMessage?.answer?.length || 0;
        }
      }
    } catch (error: any) {
      this.logger.logInfo('[AgentChatInterface] Error in ngAfterViewChecked', 'ngAfterViewChecked', error);
    }
  }

  /**
   * Cleanup on component destroy
   */
  ngOnDestroy(): void {
    try {
      this.clearRiaTextLoaderPhase();
      this.stopStreamingScroll();
      
      const chatContainer = this.autoScrollService.getChatContainer();
      if (chatContainer && this.boundScrollHandler) {
        chatContainer.removeEventListener('scroll', this.boundScrollHandler);
      }
      
      if (this.scrollStateSubscription) {
        this.scrollStateSubscription.unsubscribe();
      }
      
      this.autoScrollService.cleanup(true);
    } catch (error: any) {
      this.logger.logInfo('[AgentChatInterface] Error in ngOnDestroy', 'ngOnDestroy', error);
    }
  }

  private syncRiaTextLoaderPhase(): void {
    if (this.isRiaAgent && this.showEngagingLoader && !this.engagingLoaderIsCompleted) {
      if (!this.riaTextLoaderTimer && !this.riaTextLoaderPhaseActive) {
        this.startRiaTextLoaderPhase();
      }
      return;
    }
    this.clearRiaTextLoaderPhase();
  }

  private startRiaTextLoaderPhase(): void {
    this.clearRiaTextLoaderTimer();
    this.riaTextLoaderPhaseActive = true;
    this.riaTextLoaderTimer = setTimeout(() => {
      this.riaTextLoaderPhaseActive = false;
      this.riaTextLoaderTimer = null;
      this.cdr.detectChanges();
    }, AgentChatInterfaceComponent.RIA_TEXT_LOADER_MS);
  }

  private clearRiaTextLoaderPhase(): void {
    this.clearRiaTextLoaderTimer();
    this.riaTextLoaderPhaseActive = false;
  }

  private clearRiaTextLoaderTimer(): void {
    if (this.riaTextLoaderTimer !== null) {
      clearTimeout(this.riaTextLoaderTimer);
      this.riaTextLoaderTimer = null;
    }
  }

  /**
   * Handle scroll event on chat container
   * 
   * @param event - Scroll event
   */
  /**
   * Handle container scroll events
   * Detects scroll to top for lazy loading and manages auto-scroll behavior
   */
  private onContainerScroll(event: Event): void {
    try {
      const container = event.target as HTMLElement;
      if (!container) {
        return;
      }
      this.autoScrollService.handleUserScroll(event);
      
      const scrollTop = container.scrollTop;
      if (this.lastScrollTopFromContainer >= 0 && scrollTop < this.lastScrollTopFromContainer) {
        this.userScrolledUp.emit();
      }
      this.lastScrollTopFromContainer = scrollTop;
      
      // Check for lazy loading trigger (scroll near top)
      if (this.hasMoreHistory && !this.isLoadingMoreHistory && !this.isLazyLoadingTriggered && scrollTop <= this.lazyLoadThreshold) {
        this.isLazyLoadingTriggered = true;
        this.loadMoreHistory.emit();
        // Scroll position is restored in agent-chat-container after history merge (0/50/150ms).
        setTimeout(() => {
          this.isLazyLoadingTriggered = false;
        }, 1000);
      }
    } catch (error: any) {
      this.logger.logInfo('[AgentChatInterface] Error handling container scroll', 'onContainerScroll', error);
      this.isLazyLoadingTriggered = false;
    }
  }

  /**
   * Handle pill/suggestion button click
   * 
   * @param pillText - Text of clicked pill
   */
  onPillClick(pillText: string): void {
    try {
      if (this.loaderShow || this.isResponseStreaming || !this.isConversationSaved) {
        return;
      }
      
      this.pillClicked.emit(pillText);
    } catch (error: any) {
      this.logger.logInfo('[AgentChatInterface] Error handling pill click', 'onPillClick', error);
    }
  }

  /**
   * Scroll to bottom when user clicks scroll arrow.
   * During streaming use instant scroll so it always works and resumes auto-scroll immediately; otherwise use smooth.
   */
  scrollToBottomClick(): void {
    try {
      const last = this.ChatQueriesList?.length ? this.ChatQueriesList[this.ChatQueriesList.length - 1] : null;
      const useInstant = !!(this.loaderShow || this.isResponseStreaming || last?.isLoading);
      const behavior = useInstant ? 'auto' : 'smooth';
      this.autoScrollService.scrollToBottom(behavior);
    } catch (error: any) {
      this.logger.logInfo('[AgentChatInterface] Error in scrollToBottomClick', 'scrollToBottomClick', error);
    }
  }

  /**
   * Check if a chatdata item has non-empty metadata (sources).
   * Used to decide whether to render the metadata carousel.
   */
  hasMetadata(chatdata: any): boolean {
    if (!chatdata?.metadata) return false;
    if (Array.isArray(chatdata.metadata)) return chatdata.metadata.length > 0;
    if (typeof chatdata.metadata === 'object') return Object.keys(chatdata.metadata).length > 0;
    return false;
  }

  /**
   * TrackBy function for ChatQueriesList ngFor to help Angular detect changes
   * Simple index-based tracking to avoid change detection loops
   */
  trackByMessageId(index: number, item: any): any {
    // Use simple index-based tracking to avoid loops
    return index;
  }

  /**
   * Format date/time for display in chat history
   * Converts ISO 8601 timestamp to human-readable format
   * 
   * @param dateString - ISO 8601 format date string
   * @returns Formatted date string (e.g., "Nov 27, 2025, 06:20 UTC")
   */
  formatDateTime(dateString: string): string {
    try {
      if (!dateString) {
        return '';
      }
      
      // Keep the date in UTC without timezone conversion
      const date = new Date(dateString);
      
      if (isNaN(date.getTime())) {
        return '';
      }

      const day = date.getUTCDate().toString().padStart(2, '0'); // Use UTC date
      const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' }); // Use UTC month
      const year = date.getUTCFullYear(); // Use UTC year
      const hours = date.getUTCHours().toString().padStart(2, '0'); // Use UTC hours in 24-hour format
      const minutes = date.getUTCMinutes().toString().padStart(2, '0'); // Use UTC minutes

      const formattedDate = `${month} ${day}, ${year}, ${hours}:${minutes} UTC`;
      return formattedDate;
    } catch (e: any) {
      this.logger.logInfo('[AgentChatInterface] Error formatting date', 'format_date_time', e);
      return '';
    }
  }

  /**
   * Get agent icon path
   * @returns Agent icon URL or default chatbot icon
   */
  get agentIcon(): string {
    try {
      const agent = this.agentConfigService.getAgentConfig(this.agentId);
      return agent?.icon || '../../assets/images/chatbot-icon.svg';
    } catch (error: any) {
      this.logger.logInfo('[AgentChatInterface] Error getting agent icon', 'agentIcon', error);
      return '../../assets/images/chatbot-icon.svg';
    }
  }

  /**
   * Like click – update local state and emit to parent for API call (same as chatbot v2 LikeDisLike).
   */
  onResponseLikeClick(chatdata: ChatQueryItem, payload?: { showFeedbackPopup?: boolean }): void {
    const wasLiked = chatdata.LikeClassAdded;
    if (wasLiked) {
      // Unlike (toggle off) – reset
      chatdata.LikeClassAdded = false;
      chatdata.SubmitFeedback = false;
    } else {
      // Like – set like, clear dislike
      chatdata.LikeClassAdded = true;
      chatdata.disLikeClassAdded = false;
      // When isAgent, show feedback popup on like; do not call API yet – API is called on Submit/Skip/Close
      chatdata.SubmitFeedback = payload?.showFeedbackPopup === true;
    }
    // Emit to parent for API only when not opening the feedback popup (API is called via feedbackSubmit when popup is closed)
    if (payload?.showFeedbackPopup !== true) {
      this.feedbackLikeClick.emit({ chatdata, isLike: chatdata.LikeClassAdded ?? false });
    }
  }

  /**
   * Dislike click – toggle form or undo. Uses FeedbackOptions from config (same as chatbot v2 DisLikeclick).
   */
  onResponseDislikeClick(chatdata: ChatQueryItem): void {
    if (!chatdata.disLikeClassAdded) {
      chatdata.disLikeClassAdded = true;
      chatdata.LikeClassAdded = false;

      if (this.agentId === 'regulations_agent') {
        // Regulations agent: show feedback form with options
        chatdata.AdditionalComments = '';
        chatdata.SubmitFeedback = true;
        if (this.feedbackOptionsConfig && this.feedbackOptionsConfig.length > 0) {
          chatdata.FeedbackOptions = this.feedbackOptionsConfig.map((option: any) => ({
            ...option,
            IsChecked: false,
            pk_chathistoryid: chatdata.pk_chathistoryid
          }));
        } else {
          chatdata.FeedbackOptions = [
            { Id: 1, Name: 'Incorrect Response', IsChecked: false },
            { Id: 2, Name: 'Incorrect References', IsChecked: false },
            { Id: 3, Name: 'Missing Information', IsChecked: false },
            { Id: 4, Name: 'Irrelevant', IsChecked: false },
            { Id: 5, Name: 'Partially Correct Response', IsChecked: false }
          ];
        }
      } else {
        // Other agents (Via): show comments-only feedback form (no checkbox options)
        chatdata.AdditionalComments = '';
        chatdata.SubmitFeedback = true;
        chatdata.FeedbackOptions = [];
      }
    } else {
      // Undo dislike – clear state and emit reset to parent for API call
      chatdata.disLikeClassAdded = false;
      chatdata.SubmitFeedback = false;
      chatdata.AdditionalComments = '';
      if (chatdata.FeedbackOptions) {
        chatdata.FeedbackOptions.forEach((opt: any) => (opt.IsChecked = false));
      }
      this.feedbackDislikeUndo.emit({ chatdata });
    }
  }

  /**
   * Feedback form submitted – update local state and emit to parent for API call (same as chatbot v2 Dislikesubmit).
   */
  onResponseFeedbackSubmit(chatdata: ChatQueryItem, payload: ResponseFeedbackSubmitPayload): void {
    chatdata.SubmitFeedback = false;
    // Keep disLikeClassAdded true so (disLikeClassAdded && !SubmitFeedback) shows the thanks message
    this.feedbackSubmit.emit({ chatdata, payload });
  }

  /** Forward metadata closeHistory (no-op in agents). */
  onMetadataCloseHistory(): void {
    this.metadataCloseHistory.emit();
  }

  /** Forward metadata DisplaySourcePanel (no-op in agents). */
  onMetadataDisplaySourcePanel(event: any): void {
    this.metadataDisplaySourcePanel.emit(event);
  }

  /**
   * Feedback form cancelled – clear form state only; keep like/dislike bold.
   * When user closes popup via Skip or X, feedbackSubmit was already emitted (like saved),
   * so we only close the form (SubmitFeedback = false) and do not clear LikeClassAdded/disLikeClassAdded.
   */
  onResponseFeedbackCancel(chatdata: ChatQueryItem): void {
    if (chatdata.FeedbackOptions) {
      chatdata.FeedbackOptions.forEach((opt: any) => (opt.IsChecked = false));
    }
    chatdata.AdditionalComments = '';
    chatdata.SubmitFeedback = false;
    // Do not clear LikeClassAdded or disLikeClassAdded – keep like/dislike bold after Skip or X
  }

  /**
   * When audio-copy updates list (e.g. isSpeaking, isPaused), forward to parent.
   */
  onChatQueriesListEmit(updatedList: any): void {
    this.ChatQueriesListEmit.emit(updatedList);
  }

  /** Share clicked – emit chatdata so the container can call the share API. */
  onShareClick(chatdata: any): void {
    this.showSharePopup.emit({ chatdata });
  }

  /** 3-dots menu: Regenerate / Make crisp / Detailed – forward to parent. */
  onResponseOptionsMenuAction(chatdata: any, action: ResponseOptionsMenuAction): void {
    this.responseOptionsMenuAction.emit({ chatdata, action });
  }

  /**
   * Stop all audio playback (e.g. when switching thread, agent, or hub).
   */
  stopAllAudio(): void {
    try {
      if (this.responseActionsComponents) {
        this.responseActionsComponents.forEach(ac => ac.stopSpeaking());
      }
      this.ChatQueriesList.forEach((c: any) => {
        if (c) {
          c.isSpeaking = false;
          c.isPaused = false;
          c.utterance = null;
        }
      });
      this.cdr.detectChanges();
    } catch (error: any) {
      this.logger.logInfo('[AgentChatInterface] Error in stopAllAudio', 'stopAllAudio', error);
    }
  }
}
